import React, { useState, useMemo, useEffect } from 'react';
import { activityService } from '../services/activityService';
import { generateId } from '../utils/uuid';
import NewContactModal from '../components/NewContactModal';
import CarModeView from '../components/CarModeView';
import AnalyticsView from './AnalyticsView';
import InsightsView from './InsightsView';
import { findDuplicateClusters } from '../utils/leadDedupe';
import MergeLeadsModal from '../components/MergeLeadsModal';
import MobileContactSwipeRow from '../components/MobileContactSwipeRow';
import { loadOfflineSnapshot } from '../services/offlineSnapshotService';
import { loadSavedSessions, CallSessionOverlay, CallSessionsModal } from '../components/CallSession';
import { pullTeamCrmIntoLocal } from '../services/teamCrmCloud';
import { buildWhatsAppUrl } from '../utils/whatsappLink';
import { setViewMode } from '../utils/useIsMobile';
import DailyGoalBar from '../components/DailyGoalBar';
import {
  isReminderWebNotifEnabled,
  setReminderWebNotifEnabled,
  getNotificationPermission,
  requestReminderNotificationPermission,
} from '../services/reminderWebNotifications';

/* ── Design tokens — an Desktop-Theme gekoppelt (Liquid Glass, hell + dunkel) ──
   Akzentfarben bleiben Hex-Literale, weil sie teils mit Alpha-Suffix
   konkateniert werden (z. B. color + '20'). Neutrals kommen aus CSS-Variablen. */
const C = {
  accent:  '#159c5f',
  accentL: 'rgba(29,190,116,0.12)',
  bg:      'transparent',
  card:    'var(--glass-card)',
  text:    'var(--text)',
  text2:   'var(--text2)',
  text3:   'var(--text3)',
  border:  'var(--border2)',
  danger:  '#ef4444',
  dangerL: 'rgba(239,68,68,0.12)',
  blue:    '#159c5f',
  blueL:   'rgba(29,190,116,0.12)',
  green:   '#10b981',
  amber:   '#f59e0b',
  purple:  '#8f7cc9',
};
const GLASS = {
  backdropFilter: 'var(--glass-blur-sm)',
  WebkitBackdropFilter: 'var(--glass-blur-sm)',
};
const GLASS_LG = {
  backdropFilter: 'var(--glass-blur-lg)',
  WebkitBackdropFilter: 'var(--glass-blur-lg)',
};

/* ── Tab config ── */
const TABS = [
  { id: 'home',     label: 'Home',     icon: <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round"/> },
  { id: 'contacts', label: 'Kontakte', icon: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" strokeLinecap="round"/><path d="M23 21v-2a4 4 0 00-3-3.87" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 3.13a4 4 0 010 7.75" strokeLinecap="round" strokeLinejoin="round"/></> },
  { id: 'pipeline', label: 'Pipeline', icon: <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" strokeLinecap="round" strokeLinejoin="round"/></> },
  { id: 'tasks',    label: 'Aufgaben', icon: <><path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" strokeLinecap="round" strokeLinejoin="round"/></> },
  { id: 'search',   label: 'Suche',    icon: <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></> },
];

function Svg({ children, size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      {children}
    </svg>
  );
}

/* ─────────── Bottom Tab Bar ─────────── */
function BottomTabs({ active, onSelect, taskBadge = 0 }) {
  const activeIndex = Math.max(0, TABS.findIndex(t => t.id === active));
  return (
    <div style={{
      display: 'flex', height: 58, borderTop: '1px solid var(--glass-border)',
      background: 'var(--glass-topnav)', ...GLASS, flexShrink: 0,
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      position: 'relative',
    }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${100 / TABS.length}%`,
          height: 2.5,
          background: C.accent,
          borderRadius: 999,
          transform: `translateX(${activeIndex * 100}%)`,
          transition: 'transform var(--motion-tab-indicator) var(--motion-ease)',
        }}
      />
      {TABS.map(tab => {
        const on = active === tab.id;
        const badge = tab.id === 'tasks' && taskBadge > 0 ? taskBadge : 0;
        return (
          <button key={tab.id} onClick={() => onSelect(tab.id)} className="crm-pressable"
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'none', cursor: 'pointer', gap: 3, position: 'relative', color: on ? C.accent : C.text3, padding: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{tab.icon}</svg>
            <span style={{ fontSize: 10, fontWeight: on ? 700 : 400, letterSpacing: '0.01em' }}>{tab.label}</span>
            {badge > 0 && (
              <span style={{ position: 'absolute', top: 6, right: '50%', transform: 'translateX(8px)', background: C.danger, color: '#fff', borderRadius: 8, fontSize: 9, fontWeight: 700, padding: '1px 4px', minWidth: 14, textAlign: 'center' }}>
                {badge > 9 ? '9+' : badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────── Mobile Header ─────────── */
function Header({ title, onMenu, onBack, onAdd, notifCount = 0, onNotif }) {
  return (
    <div className="crm-mobile-fade-in" style={{ display: 'flex', alignItems: 'center', height: 52, padding: '0 14px', gap: 10, borderBottom: '1px solid var(--glass-border)', background: 'var(--glass-topnav)', ...GLASS, flexShrink: 0 }}>
      {onBack ? (
        <button onClick={onBack} className="crm-pressable" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 6, display: 'flex', alignItems: 'center', marginLeft: -6, borderRadius: 8 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
      ) : (
        <button onClick={onMenu} className="crm-pressable" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.text, padding: 6, display: 'flex', alignItems: 'center', marginLeft: -6, borderRadius: 8 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
      )}

      <div style={{ flex: 1, fontSize: 17, fontWeight: 700, color: C.text, textAlign: onBack ? 'left' : 'center' }}>{title}</div>

      <button onClick={onNotif} className="crm-pressable" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.text2, padding: 6, display: 'flex', position: 'relative', borderRadius: 8 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
        {notifCount > 0 && <span style={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, background: C.danger, borderRadius: 4, border: '1.5px solid #fff' }}/>}
      </button>
      {onAdd && (
        <button onClick={onAdd} className="crm-pressable" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 6, display: 'flex', borderRadius: 8 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
      )}
    </div>
  );
}

/* ─────────── Slide-in Drawer ─────────── */
function Drawer({ currentUser, onClose, onNavigate, onLogout, theme, onToggleTheme }) {
  const items = [
    { id: 'home',     label: 'Home',      icon: <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round"/> },
    { id: 'contacts', label: 'Kontakte',  icon: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4"/></> },
    { id: 'pipeline', label: 'Pipeline',  icon: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" strokeLinecap="round" strokeLinejoin="round"/> },
    { id: 'car_mode',      label: 'Fahrmodus',      icon: <><circle cx="12" cy="12" r="10"/><path d="M14.31 8l5.74 9.94M9.69 8h11.48M7.38 12l5.74-9.94M9.69 16L3.95 6.06M14.31 16H2.83M16.62 12l-5.74 9.94"/></> },
    { id: 'call_sessions', label: 'Call Sessions',   icon: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.63a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/> },
    { id: 'tasks',         label: 'Aufgaben',        icon: <><path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" strokeLinecap="round" strokeLinejoin="round"/></> },
    { id: 'termine',       label: 'Termine',         icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
    { id: 'analytics',    label: 'Analytics',        icon: <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></> },
    { id: 'insights',     label: 'Insights',         icon: <><path d="M3 3v18h18" strokeLinecap="round"/><path d="M7 16l4-4 3 3 5-6" strokeLinecap="round" strokeLinejoin="round"/></> },
    { id: 'desktop_view', label: 'Desktop-Ansicht',  icon: <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></> },
  ];
  const initials = (currentUser?.name || currentUser?.email || '?')[0].toUpperCase();

  return (
    <>
      <div className="crm-mobile-overlay-in" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 400, backdropFilter: 'blur(2px)' }}/>
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '78vw', maxWidth: 290, background: 'var(--glass-flyout)', ...GLASS_LG, zIndex: 401, display: 'flex', flexDirection: 'column', boxShadow: '6px 0 24px rgba(0,0,0,0.18)', animation: 'mobileDrawerIn .22s cubic-bezier(0.4,0,0.2,1)' }}>
        {/* User block */}
        <div style={{ padding: '24px 18px 16px', background: 'var(--bg3)', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 46, height: 46, borderRadius: 23, background: C.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>{currentUser?.name || 'Benutzer'}</div>
              <div style={{ fontSize: 12, color: C.text2, marginTop: 2 }}>{currentUser?.email}</div>
            </div>
          </div>
        </div>

        {/* Nav links */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          <div style={{ padding: '10px 18px 6px', fontSize: 10, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Navigation</div>
          {items.map(item => (
            <button key={item.id} onClick={() => { onNavigate(item.id); onClose(); }} className="crm-pressable"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '13px 20px',
                border: 'none',
                background: item.id === 'car_mode' ? 'linear-gradient(90deg, rgba(29,190,116,0.14), rgba(29,190,116,0.04))' : 'none',
                cursor: 'pointer',
                color: C.text,
                fontSize: 15,
                fontWeight: item.id === 'car_mode' ? 700 : 500,
                textAlign: 'left',
              }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={item.id === 'car_mode' ? C.accent : C.text2} strokeWidth="2">{item.icon}</svg>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {item.label}
                {item.id === 'car_mode' && (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.accent,
                    background: C.accentL,
                    border: `1px solid ${C.accent}55`,
                    borderRadius: 999,
                    padding: '2px 8px',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                  }}>
                    Neu
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>

        {/* Theme + Logout */}
        <div style={{ padding: 14, borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {onToggleTheme && (
            <button onClick={onToggleTheme} className="crm-pressable" style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'var(--bg4)', color: C.text, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {theme === 'dark' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
              )}
              {theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
            </button>
          )}
          <button onClick={onLogout} className="crm-pressable" style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'var(--bg3)', color: C.danger, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Abmelden
          </button>
        </div>
      </div>
    </>
  );
}

/* ─────────── Avatar ─────────── */
function Avatar({ name, size = 40 }) {
  const letter = (name || '?')[0].toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: size / 2, background: C.accentL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: C.accent, fontSize: size * 0.38, flexShrink: 0 }}>
      {letter}
    </div>
  );
}

/* ─────────── Skeleton (Cloud-Erstladen) ─────────── */
function SkeletonRows({ rows = 8 }) {
  return (
    <div style={{ flex: 1, overflow: 'hidden' }}>
      <style>{`@keyframes crmSkeletonPulse { 0%,100% { opacity: 0.45; } 50% { opacity: 0.9; } }`}</style>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '14px 16px', borderBottom: `1px solid ${C.border}`, background: C.card, animation: 'crmSkeletonPulse 1.4s ease-in-out infinite', animationDelay: `${i * 90}ms` }}>
          <div style={{ width: 42, height: 42, borderRadius: 21, background: 'var(--bg4)', flexShrink: 0 }}/>
          <div style={{ flex: 1 }}>
            <div style={{ height: 13, width: `${55 + (i % 3) * 12}%`, borderRadius: 6, background: 'var(--bg4)', marginBottom: 8 }}/>
            <div style={{ height: 10, width: `${30 + (i % 4) * 10}%`, borderRadius: 5, background: 'var(--bg4)' }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────── Bottom Sheet ─────────── */
function Sheet({ title, onClose, children }) {
  return (
    <>
      <div className="crm-mobile-overlay-in" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 500, backdropFilter: 'blur(2px)' }}/>
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 501, background: 'var(--glass-modal)', ...GLASS_LG, borderRadius: '18px 18px 0 0', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.18)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', animation: 'mobileSheetIn .24s cubic-bezier(0.4,0,0.2,1)' }}>
        <style>{`@keyframes mobileSheetIn { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: C.border, margin: '10px auto 0', flexShrink: 0 }}/>
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 18px 10px', flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text, flex: 1 }}>{title}</div>
          <button onClick={onClose} className="crm-pressable" style={{ width: 30, height: 30, borderRadius: 15, background: 'var(--bg4)', border: 'none', cursor: 'pointer', color: C.text2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>{children}</div>
      </div>
    </>
  );
}

/* ─────────── Status Picker Sheet ─────────── */
function StatusPickerSheet({ lead, statusMeta, onSelect, onClose }) {
  return (
    <Sheet title="Status ändern" onClose={onClose}>
      <div style={{ padding: '4px 12px 18px' }}>
        {Object.entries(statusMeta || {}).map(([key, meta]) => {
          const active = lead.status === key;
          return (
            <button key={key} onClick={() => { onSelect(key); onClose(); }} className="crm-pressable"
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', borderRadius: 12, border: `1.5px solid ${active ? (meta.color || C.accent) : 'transparent'}`, background: active ? (meta.color || C.accent) + '14' : 'none', cursor: 'pointer', marginBottom: 2, textAlign: 'left' }}>
              <div style={{ width: 12, height: 12, borderRadius: 6, background: meta.color || C.text3, flexShrink: 0 }}/>
              <span style={{ fontSize: 15, fontWeight: active ? 700 : 500, color: C.text, flex: 1 }}>{meta.label}</span>
              {active && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={meta.color || C.accent} strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>}
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}

/* ─────────── Edit Lead Sheet ─────────── */
function EditLeadSheet({ lead, onSave, onClose }) {
  const [form, setForm] = useState(() => ({
    name: lead.name || '', company: lead.company || '', tel: lead.tel === '–' ? '' : (lead.tel || ''),
    email: lead.email === '–' ? '' : (lead.email || ''), city: lead.city || '',
    wiedervorlage: lead.wiedervorlage ? String(lead.wiedervorlage).slice(0, 10) : '',
  }));
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const fields = [
    { key: 'name',    label: 'Name',        type: 'text' },
    { key: 'company', label: 'Unternehmen', type: 'text' },
    { key: 'tel',     label: 'Telefon',     type: 'tel' },
    { key: 'email',   label: 'E-Mail',      type: 'email' },
    { key: 'city',    label: 'Stadt',       type: 'text' },
    { key: 'wiedervorlage', label: 'Wiedervorlage', type: 'date' },
  ];

  function handleSave() {
    const changes = {};
    if (form.name.trim())            changes.name    = form.name.trim();
    changes.company = form.company.trim();
    changes.tel     = form.tel.trim() || '–';
    changes.email   = form.email.trim() || '–';
    changes.city    = form.city.trim();
    changes.wiedervorlage = form.wiedervorlage || null;
    onSave(changes, Object.keys(changes));
    onClose();
  }

  return (
    <Sheet title="Kontakt bearbeiten" onClose={onClose}>
      <div style={{ padding: '4px 18px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {fields.map(f => (
          <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{f.label}</span>
            <input type={f.type} value={form[f.key]} onChange={set(f.key)}
              style={{ height: 44, borderRadius: 10, border: `1.5px solid ${C.border}`, background: 'var(--bg3)', padding: '0 12px', fontSize: 15, color: C.text, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}/>
          </label>
        ))}
        <button onClick={handleSave} className="crm-pressable"
          style={{ height: 48, marginTop: 4, borderRadius: 12, border: 'none', background: C.accent, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          Speichern
        </button>
      </div>
    </Sheet>
  );
}

/* ─────────── Notifications Sheet ─────────── */
function NotificationsSheet({ notifications, onMarkAllRead, onClose }) {
  const sorted = [...notifications].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  const [webNotif, setWebNotif] = useState(() => isReminderWebNotifEnabled() && getNotificationPermission() === 'granted');
  const notifSupported = getNotificationPermission() !== 'unsupported';

  async function toggleWebNotif() {
    if (webNotif) {
      setReminderWebNotifEnabled(false);
      setWebNotif(false);
      return;
    }
    const p = await requestReminderNotificationPermission();
    if (p === 'granted') {
      setReminderWebNotifEnabled(true);
      setWebNotif(true);
    }
  }

  return (
    <Sheet title="Benachrichtigungen" onClose={onClose}>
      <div style={{ padding: '0 12px 18px' }}>
        {notifSupported && (
          <button onClick={toggleWebNotif} className="crm-pressable"
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, border: `1.5px solid ${webNotif ? C.accent + '55' : 'var(--border2)'}`, background: webNotif ? C.accentL : 'var(--bg4)', cursor: 'pointer', marginBottom: 12, textAlign: 'left' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={webNotif ? C.accent : 'var(--text2)'} strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Erinnerungen als Mitteilung</div>
              <div style={{ fontSize: 12, color: C.text2, marginTop: 1 }}>Fällige Aufgaben & Wiedervorlagen{webNotif ? ' — aktiv' : ''}</div>
            </div>
            <div style={{ width: 40, height: 24, borderRadius: 12, background: webNotif ? C.accent : 'var(--bg4)', border: `1px solid ${webNotif ? C.accent : 'var(--border2)'}`, position: 'relative', flexShrink: 0, transition: 'background .15s' }}>
              <div style={{ position: 'absolute', top: 2, left: webNotif ? 18 : 2, width: 18, height: 18, borderRadius: 9, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left .15s' }}/>
            </div>
          </button>
        )}
        {sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 0', color: C.text3, fontSize: 14 }}>Keine Benachrichtigungen</div>
        ) : (
          <>
            <button onClick={() => { onMarkAllRead(); }} className="crm-pressable"
              style={{ width: '100%', height: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: 'var(--bg4)', color: C.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}>
              Alle als gelesen markieren
            </button>
            {sorted.map(n => (
              <div key={n.id} style={{ padding: '12px 12px', borderRadius: 12, background: n.read ? 'none' : C.accentL, marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {!n.read && <div style={{ width: 7, height: 7, borderRadius: 4, background: C.accent, flexShrink: 0 }}/>}
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text, flex: 1 }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: C.text3, flexShrink: 0 }}>
                    {n.createdAt ? new Date(n.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : ''}
                  </div>
                </div>
                {n.message && <div style={{ fontSize: 13, color: C.text2, marginTop: 3 }}>{n.message}</div>}
              </div>
            ))}
          </>
        )}
      </div>
    </Sheet>
  );
}

/* ─────────── Termine View ─────────── */
function TermineView({ meetings, leads, onSaveMeeting, onOpenContact }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: '', leadId: '', date: '', time: '', meetingType: 'Termin' });

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const withDate = (meetings || []).map(m => {
      let dt = null;
      if (m.startISO) dt = new Date(m.startISO);
      else if (m.dateISO) dt = new Date(`${m.dateISO}T${m.startTime || '09:00'}`);
      else if (m.date && /^\d{2}\.\d{2}\.\d{4}$/.test(m.date)) {
        const [d, mo, y] = m.date.split('.');
        dt = new Date(`${y}-${mo}-${d}T${m.startTime || '09:00'}`);
      }
      if (dt && Number.isNaN(dt.getTime())) dt = null;
      return { ...m, _dt: dt };
    });
    const up = withDate.filter(m => m._dt && m._dt >= now).sort((a, b) => a._dt - b._dt);
    const pa = withDate.filter(m => !m._dt || m._dt < now).sort((a, b) => (b._dt || 0) - (a._dt || 0)).slice(0, 15);
    return { upcoming: up, past: pa };
  }, [meetings]);

  function fmtMeeting(m) {
    if (!m._dt) return '';
    return m._dt.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short' }) +
      ' · ' + m._dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  function handleCreate() {
    if (!form.title.trim() || !form.date) return;
    const lead = leads.find(l => String(l.id) === String(form.leadId));
    const startTime = form.time || '09:00';
    const [h, min] = startTime.split(':').map(Number);
    const endTime = `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    const parts = form.date.split('-');
    // Gleiches Datenformat wie FloatingMeeting (Desktop)
    onSaveMeeting({
      type: 'meeting',
      title: form.title.trim(),
      meetingType: form.meetingType || 'Vorort-Besuch',
      date: `${parts[2]}.${parts[1]}.${parts[0]}`,
      dateISO: form.date,
      startTime,
      endTime,
      startISO: new Date(`${form.date}T${startTime}`).toISOString(),
      endISO: new Date(`${form.date}T${endTime}`).toISOString(),
      lead: lead ? { id: lead.id, name: lead.name, company: lead.company } : null,
      month: new Date(form.date).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
    });
    setForm({ title: '', leadId: '', date: '', time: '', meetingType: 'Termin' });
    setShowNew(false);
  }

  function MeetingCard({ m, muted }) {
    const leadRef = m.lead?.id ?? m.leadId;
    const lead = leadRef != null ? leads.find(l => String(l.id) === String(leadRef)) : null;
    return (
      <div style={{ background: C.card, borderRadius: 12, padding: '13px 14px', marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', opacity: muted ? 0.65 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: C.blueL, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title || m.meetingType || 'Termin'}</div>
            <div style={{ fontSize: 12, color: C.text2, marginTop: 1 }}>{fmtMeeting(m)}{(m.lead?.name || m.leadName) ? ` · ${m.lead?.name || m.leadName}` : ''}</div>
          </div>
          {lead && (
            <button onClick={() => onOpenContact(lead)} className="crm-pressable" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.text3, padding: 6 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round"/></svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg, padding: '12px 12px 24px' }}>
      <button onClick={() => setShowNew(true)} className="crm-pressable"
        style={{ width: '100%', height: 46, borderRadius: 12, border: `1.5px dashed ${C.accent}66`, background: C.accentL, color: C.accent, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 14 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Neuer Termin
      </button>

      {upcoming.length > 0 && <div style={{ fontSize: 12, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 4px 8px' }}>Anstehend ({upcoming.length})</div>}
      {upcoming.map(m => <MeetingCard key={m.id} m={m}/>)}
      {upcoming.length === 0 && (
        <div style={{ textAlign: 'center', padding: '28px 0', color: C.text3, fontSize: 14 }}>Keine anstehenden Termine</div>
      )}

      {past.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '16px 4px 8px' }}>Vergangen</div>
          {past.map(m => <MeetingCard key={m.id} m={m} muted/>)}
        </>
      )}

      {showNew && (
        <Sheet title="Neuer Termin" onClose={() => setShowNew(false)}>
          <div style={{ padding: '4px 18px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Titel</span>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="z. B. Erstgespräch"
                style={{ height: 44, borderRadius: 10, border: `1.5px solid ${C.border}`, background: 'var(--bg3)', padding: '0 12px', fontSize: 15, color: C.text, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}/>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Kontakt (optional)</span>
              <select value={form.leadId} onChange={e => setForm(f => ({ ...f, leadId: e.target.value }))}
                style={{ height: 44, borderRadius: 10, border: `1.5px solid ${C.border}`, background: 'var(--bg3)', padding: '0 8px', fontSize: 15, color: C.text, outline: 'none', fontFamily: 'inherit' }}>
                <option value="">— Kein Kontakt —</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.name}{l.company ? ` (${l.company})` : ''}</option>)}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Datum</span>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  style={{ height: 44, borderRadius: 10, border: `1.5px solid ${C.border}`, background: 'var(--bg3)', padding: '0 12px', fontSize: 15, color: C.text, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' }}/>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Uhrzeit</span>
                <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                  style={{ height: 44, borderRadius: 10, border: `1.5px solid ${C.border}`, background: 'var(--bg3)', padding: '0 12px', fontSize: 15, color: C.text, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' }}/>
              </label>
            </div>
            <button onClick={handleCreate} disabled={!form.title.trim() || !form.date} className="crm-pressable"
              style={{ height: 48, marginTop: 4, borderRadius: 12, border: 'none', background: (!form.title.trim() || !form.date) ? C.border : C.accent, color: (!form.title.trim() || !form.date) ? C.text3 : '#fff', fontSize: 15, fontWeight: 700, cursor: (!form.title.trim() || !form.date) ? 'default' : 'pointer' }}>
              Termin speichern
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

/* ─────────── Contact Detail ─────────── */
function ContactDetail({ lead, statusMeta, onUpdateLead, onSaveTask, onQuickTask }) {
  const [tab, setTab] = useState('info');
  const [activities, setActivities] = useState(() => activityService.getForLead(lead.id));
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const meta = statusMeta?.[lead.status] || { label: lead.status, color: C.text2 };

  useEffect(() => {
    setActivities(activityService.getForLead(lead.id));
  }, [lead.id]);

  useEffect(() => {
    function reload() {
      setActivities(activityService.getForLead(lead.id));
    }
    function onRemote(e) {
      const lid = e?.detail?.leadId;
      if (lid === '__all__' || (lid != null && String(lid) === String(lead.id))) reload();
    }
    function onStorage(ev) {
      if (ev.key === 'crm_activities') reload();
    }
    window.addEventListener('crm-activities-changed', onRemote);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('crm-activities-changed', onRemote);
      window.removeEventListener('storage', onStorage);
    };
  }, [lead.id]);

  function saveNote() {
    if (!note.trim()) return;
    const act = { id: generateId(), type: 'note', text: note.trim(), date: new Date().toLocaleDateString('de-DE'), timestamp: new Date().toISOString() };
    const next = [act, ...activities];
    setActivities(next);
    activityService.saveForLead(lead.id, next);
    setNote(''); setShowNote(false);
  }

  const waUrl = buildWhatsAppUrl(lead);
  const PHONE_ICON = <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.63a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" strokeLinecap="round" strokeLinejoin="round"/>;
  const quickActions = [
    { label: 'Anrufen',  color: C.blue,   icon: PHONE_ICON, action: () => { if (!lead.tel || lead.tel === '–') return; const a = document.createElement('a'); a.href = `tel:${lead.tel}`; a.click(); } },
    { label: 'E-Mail',   color: C.green,  icon: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>, action: () => { if (!lead.email || lead.email === '–') return; const a = document.createElement('a'); a.href = `mailto:${lead.email}`; a.click(); } },
    ...(waUrl ? [{ label: 'WhatsApp', color: '#25d366', icon: <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" strokeLinecap="round" strokeLinejoin="round"/>, action: () => window.open(waUrl, '_blank', 'noopener,noreferrer') }] : []),
    { label: 'Notiz',    color: C.amber,  icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>, action: () => { setShowNote(v => !v); setTab('activity'); } },
    { label: 'Aufgabe',  color: C.purple, icon: <><path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" strokeLinecap="round" strokeLinejoin="round"/></>, action: () => onQuickTask?.(lead) },
  ];

  const infoFields = [
    { label: 'E-Mail', value: lead.email, href: lead.email !== '–' ? `mailto:${lead.email}` : null, icon: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></> },
    { label: 'Telefon', value: lead.tel, href: lead.tel !== '–' ? `tel:${lead.tel}` : null, icon: PHONE_ICON },
    { label: 'Unternehmen', value: lead.company, icon: <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round"/> },
    { label: 'Stadt', value: lead.city, icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></> },
    { label: 'Wiedervorlage', value: lead.wiedervorlage ? new Date(lead.wiedervorlage).toLocaleDateString('de-DE') : null, icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
    { label: 'Score', value: lead.score > 0 ? `${lead.score} Punkte` : null, icon: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/> },
  ].filter(f => f.value && f.value !== '–');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bg }}>
      {/* Hero */}
      <div style={{ background: C.card, padding: '16px 16px 14px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <Avatar name={lead.name} size={52}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{lead.name}</div>
            <div style={{ fontSize: 14, color: C.text2 }}>{lead.company}</div>
            <button onClick={() => setShowStatusPicker(true)} className="crm-pressable"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5, padding: '4px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700, background: (meta.color || C.accent) + '20', color: meta.color || C.accent, border: `1px solid ${(meta.color || C.accent)}35`, cursor: 'pointer' }}>
              {meta.label}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>
            </button>
          </div>
          <button onClick={() => setShowEdit(true)} className="crm-pressable" title="Bearbeiten"
            style={{ width: 38, height: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: 'var(--bg4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.text2, flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
        </div>
        {/* Quick actions */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${quickActions.length}, 1fr)`, gap: 6 }}>
          {quickActions.map(qa => (
            <button key={qa.label} onClick={qa.action} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '9px 3px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'var(--bg4)', cursor: 'pointer' }}>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke={qa.color} strokeWidth="2">{qa.icon}</svg>
              <span style={{ fontSize: 10, color: C.text2, fontWeight: 500, lineHeight: 1.2, textAlign: 'center' }}>{qa.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: C.card, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        {[{ id: 'info', label: 'Details' }, { id: 'activity', label: 'Aktivitäten' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: '12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: tab === t.id ? C.accent : C.text2, borderBottom: `2px solid ${tab === t.id ? C.accent : 'transparent'}`, transition: 'all .12s' }}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {tab === 'info' && (
          <div>
            {infoFields.map(f => (
              <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderBottom: `1px solid ${C.border}`, background: C.card }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--bg4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.text2} strokeWidth="2">{f.icon}</svg>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.text3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{f.label}</div>
                  {f.href
                    ? <a href={f.href} style={{ fontSize: 14, color: C.accent, fontWeight: 500, textDecoration: 'none' }}>{f.value}</a>
                    : <div style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>{f.value}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === 'activity' && (
          <div style={{ padding: '12px 14px' }}>
            {showNote && (
              <div style={{ background: C.card, borderRadius: 12, padding: 12, marginBottom: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Notiz schreiben…" autoFocus
                  style={{ width: '100%', minHeight: 80, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 14, fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box', color: C.text }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowNote(false)} style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer', fontSize: 13, color: C.text2 }}>Abbrechen</button>
                  <button onClick={saveNote} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: C.accent, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Speichern</button>
                </div>
              </div>
            )}
            {activities.length === 0 ? (
              <div style={{ textAlign: 'center', color: C.text3, padding: '40px 0', fontSize: 14 }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4, marginBottom: 10 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <div>Noch keine Aktivitäten</div>
              </div>
            ) : activities.map(act => (
              <div key={act.id} style={{ background: C.card, borderRadius: 12, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: act.type === 'call' ? C.blue : act.type === 'email' ? C.green : C.text3, background: act.type === 'call' ? C.blueL : act.type === 'email' ? 'rgba(16,185,129,0.12)' : '#f0f5f0', padding: '2px 7px', borderRadius: 5 }}>
                    {act.type === 'call' ? 'Anruf' : act.type === 'email' ? 'E-Mail' : act.type === 'meeting' ? 'Termin' : 'Notiz'}
                  </span>
                  <span style={{ fontSize: 11, color: C.text3, marginLeft: 'auto' }}>{act.date}</span>
                </div>
                <div style={{ fontSize: 14, color: C.text, lineHeight: 1.5 }}>{act.text || act.note || act.subject || ''}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showStatusPicker && (
        <StatusPickerSheet
          lead={lead}
          statusMeta={statusMeta}
          onSelect={status => onUpdateLead?.(lead.id, { status, _statusChangedAt: new Date().toISOString() }, ['status'])}
          onClose={() => setShowStatusPicker(false)}
        />
      )}
      {showEdit && (
        <EditLeadSheet
          lead={lead}
          onSave={(changes, fields) => onUpdateLead?.(lead.id, changes, fields)}
          onClose={() => setShowEdit(false)}
        />
      )}
    </div>
  );
}

/* ─────────── Mobile Call Session Card ─────────── */
function CallSessionCard({ onOpenSessions }) {
  const [count, setCount] = useState(() => loadSavedSessions().length);
  useEffect(() => {
    const update = () => setCount(loadSavedSessions().length);
    window.addEventListener('focus', update);
    window.addEventListener('crm-sessions-updated', update);
    return () => {
      window.removeEventListener('focus', update);
      window.removeEventListener('crm-sessions-updated', update);
    };
  }, []);
  if (count === 0) return null;
  return (
    <button
      type="button"
      onClick={onOpenSessions}
      className="crm-pressable"
      style={{
        margin: '8px 12px 0',
        background: 'linear-gradient(135deg, rgba(29,190,116,0.12), rgba(15,138,83,0.06))',
        border: '1.5px solid rgba(29,190,116,0.3)',
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: 'calc(100% - 24px)',
        cursor: 'pointer',
        textAlign: 'left',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(29,190,116,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#159c5f" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.63a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Call Sessions fortsetzen</div>
        <div style={{ fontSize: 12, color: '#159c5f', marginTop: 2, fontWeight: 600 }}>{count} pausierte Session{count !== 1 ? 's' : ''} — Tippen zum Öffnen</div>
      </div>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#159c5f" strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round"/></svg>
    </button>
  );
}

/* ─────────── Home View ─────────── */
function HomeView({ leads, tasks, meetings, statusMeta, onOpenContact, onOpenTab, onToggleTask, onOpenSessions, currentUser, authUsers }) {
  const now = new Date();
  const overdueCount = tasks.filter(t => !t.done && t.dueDatetime && new Date(t.dueDatetime) < now).length;
  const openCount    = tasks.filter(t => !t.done).length;
  const upcomingList = tasks.filter(t => !t.done).slice(0, 6);
  const recentList   = leads.slice(0, 5);

  const tiles = [
    { label: 'Kontakte', value: leads.length,                                      color: C.blue,   icon: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" strokeLinecap="round"/><circle cx="9" cy="7" r="4"/></>, tab: 'contacts' },
    { label: 'Neu',      value: leads.filter(l => l.status === 'new').length,       color: C.green,  icon: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16" strokeLinecap="round"/><line x1="8" y1="12" x2="16" y2="12" strokeLinecap="round"/></>, tab: 'contacts' },
    { label: 'Aufgaben', value: openCount,                                          color: C.amber,  icon: <><path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" strokeLinecap="round" strokeLinejoin="round"/></>, tab: 'tasks' },
    { label: 'Überfällig', value: overdueCount,                                     color: C.danger, icon: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12" strokeLinecap="round"/><line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round"/></>, tab: 'tasks' },
  ];

  function fmtDue(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' }) + ' · ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      {/* Tagesziele + Streak */}
      <div style={{ padding: '12px 12px 0' }}>
        <DailyGoalBar currentUser={currentUser} authUsers={authUsers}/>
      </div>
      {/* Saved Call Sessions card */}
      <CallSessionCard onOpenSessions={onOpenSessions} />
      {/* Quick tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, padding: '14px 14px 6px' }}>
        {tiles.map(t => (
          <button key={t.label} onClick={() => onOpenTab(t.tab)} className="crm-card crm-pressable" style={{ background: C.card, border: 'none', borderRadius: 14, padding: '12px 4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: t.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={t.color} strokeWidth="2">{t.icon}</svg>
            </div>
            <span style={{ fontSize: 18, fontWeight: 700, color: C.text, lineHeight: 1 }}>{t.value}</span>
            <span style={{ fontSize: 10, color: C.text2, fontWeight: 500 }}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Upcoming tasks */}
        {upcomingList.length > 0 && (
        <div style={{ margin: '8px 12px', background: C.card, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Anstehende Aufgaben</div>
              <div style={{ fontSize: 12, color: overdueCount > 0 ? C.danger : C.text2, marginTop: 1, fontWeight: overdueCount > 0 ? 600 : 400 }}>
                {overdueCount > 0 ? `${overdueCount} überfällig, ` : ''}{openCount} offen
              </div>
            </div>
            <button onClick={() => onOpenTab('tasks')} className="crm-pressable" style={{ background: 'none', border: 'none', color: C.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Alle →</button>
          </div>
          {upcomingList.map((task, idx) => {
            const overdue = task.dueDatetime && new Date(task.dueDatetime) < now;
            return (
              <div key={task.id} className="crm-list-item-in" style={{ animationDelay: `${Math.min(idx, 6) * 20}ms`, padding: '11px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => onToggleTask(task.id)} className="crm-pressable" style={{ width: 22, height: 22, borderRadius: 11, border: `2px solid ${overdue ? C.danger : C.border}`, background: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {task.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
                  {task.leadName && <div style={{ fontSize: 12, color: C.text2 }}>{task.leadName}</div>}
                  {task.dueDatetime && <div style={{ fontSize: 11, color: overdue ? C.danger : C.text3, fontWeight: overdue ? 700 : 400, marginTop: 1 }}>{fmtDue(task.dueDatetime)}</div>}
                </div>
                {overdue && <span style={{ background: C.dangerL, color: C.danger, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, flexShrink: 0 }}>FÄLLIG</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Recent contacts */}
      {recentList.length > 0 && (
        <div style={{ margin: '8px 12px 20px', background: C.card, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Letzte Kontakte</div>
            <button onClick={() => onOpenTab('contacts')} className="crm-pressable" style={{ background: 'none', border: 'none', color: C.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Alle →</button>
          </div>
          {recentList.map((lead, idx) => (
            <button key={lead.id} onClick={() => onOpenContact(lead)} className="crm-pressable crm-list-item-in" style={{ animationDelay: `${Math.min(idx, 6) * 18}ms`, width: '100%', padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', borderTop: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left' }}>
              <Avatar name={lead.name} size={38}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
                <div style={{ fontSize: 12, color: C.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.company}</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round"/></svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────── Contacts View ─────────── */
const MOBILE_CONTACTS_PAGE = 80;

function ContactsView({ leads, statusMeta, lists, onUpdateLead, onSaveTask, onOpenContact, onMergeLeads, onQuickTask, onQuickNote, onQuickMail, tableDensity = 'comfortable', onStartSession }) {
  const [q, setQ] = useState('');
  const [mergeClusters, setMergeClusters] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const dense = tableDensity === 'compact';

  const filtered = useMemo(() => {
    const list = leads || [];
    if (!q.trim()) return list;
    const ql = q.toLowerCase();
    return list.filter(l =>
      (l.name || '').toLowerCase().includes(ql) ||
      (l.company || '').toLowerCase().includes(ql) ||
      (l.email || '').toLowerCase().includes(ql)
    );
  }, [leads, q]);

  const visible = useMemo(() => {
    if (showAll || q.trim()) return filtered;
    return filtered.slice(0, MOBILE_CONTACTS_PAGE);
  }, [filtered, showAll, q]);

  return (
    <>
      {/* Search bar */}
      <div style={{ padding: '10px 14px', background: C.bg, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1, background: C.card, borderRadius: 10, padding: '9px 12px', gap: 8, border: `1px solid ${C.border}` }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={q} onChange={e => { setQ(e.target.value); setShowAll(false); }} placeholder="Suche…" style={{ border: 'none', outline: 'none', fontSize: 15, flex: 1, background: 'transparent', color: C.text }}/>
            {q && <button onClick={() => { setQ(''); setShowAll(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.text3, padding: 0, fontSize: 16, lineHeight: 1 }}>×</button>}
          </div>
          {onStartSession && filtered.length > 0 && (
            <button type="button" onClick={() => onStartSession(filtered)} className="crm-pressable"
              title="Call Session starten"
              style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, border: `1.5px solid ${C.accent}`, background: C.accentL, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accent }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.63a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
            </button>
          )}
          {onMergeLeads && (
            <button type="button" onClick={() => {
              const clusters = findDuplicateClusters(leads);
              if (!clusters.length) return;
              setMergeClusters(clusters);
            }} className="crm-pressable" style={{ flexShrink: 0, height: 40, padding: '0 10px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, fontSize: 11, fontWeight: 600, color: C.text2, cursor: 'pointer' }}>
              Duplikate
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: '6px 16px 4px', fontSize: 12, color: C.text3, background: C.bg, flexShrink: 0 }}>{filtered.length} Kontakt{filtered.length !== 1 ? 'e' : ''}</div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
        {visible.map((lead, idx) => {
          const waUrl = buildWhatsAppUrl(lead);
          return (
          <MobileContactSwipeRow
            key={lead.id}
            hasTel={!!lead.tel && lead.tel !== '–'}
            hasTask={!!onQuickTask}
            hasNote={!!(onQuickNote || onQuickMail)}
            hasWhatsApp={!!waUrl}
            onCall={() => { if (!lead.tel) return; const a = document.createElement('a'); a.href = `tel:${String(lead.tel).replace(/\s+/g, '')}`; a.click(); }}
            onTask={() => onQuickTask?.(lead)}
            onNote={() => (onQuickNote ? onQuickNote(lead) : onQuickMail?.(lead))}
            onWhatsApp={() => window.open(waUrl, '_blank', 'noopener,noreferrer')}
          >
            <button type="button" onClick={() => onOpenContact(lead)} className="crm-pressable crm-list-item-in" style={{ animationDelay: `${Math.min(idx, 8) * 14}ms`, width: '100%', background: C.card, border: 'none', borderBottom: `1px solid ${C.border}`, padding: dense ? '10px 12px' : '14px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 13 }}>
              <Avatar name={lead.name} size={dense ? 36 : 42}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: dense ? 14 : 15, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
                <div style={{ fontSize: dense ? 12 : 13, color: C.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{lead.company}</div>
                <div style={{ fontSize: dense ? 11 : 12, color: C.text3, marginTop: 3 }}>{lead.activity}</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round"/></svg>
            </button>
          </MobileContactSwipeRow>
          );
        })}
        {filtered.length === 0 && <div style={{ textAlign: 'center', padding: '48px 24px', color: C.text3, fontSize: 14 }}>Keine Kontakte gefunden</div>}
        {!showAll && !q.trim() && filtered.length > MOBILE_CONTACTS_PAGE && (
          <button type="button" onClick={() => setShowAll(true)} style={{ width: '100%', padding: '16px', background: C.card, border: 'none', borderTop: `1px solid ${C.border}`, color: C.accent, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Alle {filtered.length} Kontakte anzeigen
          </button>
        )}
      </div>
      {mergeClusters?.length > 0 && (
        <MergeLeadsModal
          clusters={mergeClusters}
          onClose={() => setMergeClusters(null)}
          onMerge={(primaryId, secondaryIds) => {
            onMergeLeads?.(primaryId, secondaryIds);
            setMergeClusters(null);
          }}
        />
      )}
    </>
  );
}

/* ─────────── Pipeline View ─────────── */
function PipelineView({ leads, statusMeta, onOpenLead }) {
  const [expanded, setExpanded] = useState(null);

  const groups = useMemo(() => {
    const g = {};
    Object.entries(statusMeta).forEach(([k, v]) => { g[k] = { meta: v, leads: [] }; });
    leads.forEach(l => { if (g[l.status]) g[l.status].leads.push(l); });
    return Object.entries(g).filter(([, v]) => v.leads.length > 0);
  }, [leads, statusMeta]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg, padding: '12px 12px 24px' }}>
      {groups.map(([status, { meta, leads: gl }]) => (
        <div key={status} style={{ background: C.card, borderRadius: 14, overflow: 'hidden', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <button onClick={() => setExpanded(expanded === status ? null : status)} style={{ width: '100%', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}>
            <div style={{ width: 12, height: 12, borderRadius: 6, background: meta.color, flexShrink: 0 }}/>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text, flex: 1, textAlign: 'left' }}>{meta.label}</span>
            <span style={{ fontSize: 14, color: C.text2, fontWeight: 500 }}>{gl.length}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="2" strokeLinecap="round">{expanded === status ? <path d="M18 15l-6-6-6 6"/> : <path d="M6 9l6 6 6-6"/>}</svg>
          </button>
          {expanded === status && gl.map(lead => (
            <button key={lead.id} onClick={() => onOpenLead?.(lead)} className="crm-pressable"
              style={{ width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', borderTop: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left' }}>
              <Avatar name={lead.name} size={36}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
                <div style={{ fontSize: 12, color: C.text2 }}>{lead.company}</div>
              </div>
              {lead.score > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: C.amber }}>★ {lead.score}</span>}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round"/></svg>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─────────── Tasks View ─────────── */
function TasksView({ tasks, onToggleTask }) {
  const now = new Date();
  const overdue  = tasks.filter(t => !t.done && t.dueDatetime && new Date(t.dueDatetime) < now);
  const upcoming = tasks.filter(t => !t.done && !(t.dueDatetime && new Date(t.dueDatetime) < now));
  const done     = tasks.filter(t => t.done).slice(0, 8);

  function fmtDue(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  function TaskItem({ task }) {
    const od = !task.done && task.dueDatetime && new Date(task.dueDatetime) < now;
    return (
      <div className="crm-list-item-in" style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => onToggleTask(task.id)} className="crm-pressable" style={{ width: 24, height: 24, borderRadius: 12, border: `2px solid ${task.done ? C.accent : od ? C.danger : C.border}`, background: task.done ? C.accent : 'transparent', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {task.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: task.done ? C.text3 : C.text, textDecoration: task.done ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
          {task.leadName && <div style={{ fontSize: 12, color: C.text2 }}>{task.leadName}</div>}
          {task.dueDatetime && <div style={{ fontSize: 11, color: od ? C.danger : C.text3, fontWeight: od ? 700 : 400 }}>{fmtDue(task.dueDatetime)}{od ? ' · Überfällig' : ''}</div>}
        </div>
        {od && <span style={{ background: C.dangerL, color: C.danger, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5 }}>FÄLLIG</span>}
      </div>
    );
  }

  function Section({ title, items, color }) {
    if (!items.length) return null;
    return (
      <div style={{ margin: '8px 12px 0', background: C.card, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '12px 16px 8px', fontSize: 14, fontWeight: 700, color: color || C.text }}>
          {title} <span style={{ color: C.text3, fontWeight: 500 }}>({items.length})</span>
        </div>
        {items.map(t => <TaskItem key={t.id} task={t}/>)}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg, paddingBottom: 20 }}>
      <Section title="Überfällig" items={overdue} color={C.danger}/>
      <Section title="Offen" items={upcoming}/>
      <Section title="Erledigt" items={done}/>
      {!overdue.length && !upcoming.length && !done.length && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: C.text3, fontSize: 14 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3, marginBottom: 12 }}><path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <div>Keine Aufgaben</div>
        </div>
      )}
    </div>
  );
}

/* ─────────── Search View ─────────── */
function SearchView({ leads, onOpenContact }) {
  const [q, setQ] = useState('');

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const ql = q.toLowerCase();
    return leads.filter(l =>
      (l.name || '').toLowerCase().includes(ql) ||
      (l.company || '').toLowerCase().includes(ql) ||
      (l.email || '').toLowerCase().includes(ql) ||
      (l.tel || '').replace(/\s/g, '').includes(ql.replace(/\s/g, ''))
    ).slice(0, 20);
  }, [leads, q]);

  return (
    <>
      <div style={{ padding: '12px 14px', background: C.card, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg3)', borderRadius: 10, padding: '10px 14px', gap: 10, border: `1px solid ${C.border}` }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Kontakt, E-Mail, Telefon…" style={{ border: 'none', outline: 'none', fontSize: 16, flex: 1, background: 'transparent', color: C.text }}/>
          {q && <button onClick={() => setQ('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.text3, fontSize: 18, lineHeight: 1 }}>×</button>}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {q && results.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: C.text3, fontSize: 14 }}>Keine Ergebnisse für „{q}"</div>
        )}
        {results.map((lead, idx) => (
          <button key={lead.id} onClick={() => onOpenContact(lead)} className="crm-pressable crm-list-item-in" style={{ animationDelay: `${Math.min(idx, 8) * 14}ms`, width: '100%', background: C.card, border: 'none', borderBottom: `1px solid ${C.border}`, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 13 }}>
            <Avatar name={lead.name} size={40}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
              <div style={{ fontSize: 13, color: C.text2 }}>{lead.company}</div>
              {lead.email && lead.email !== '–' && <div style={{ fontSize: 12, color: C.text3 }}>{lead.email}</div>}
            </div>
          </button>
        ))}
        {!q && (
          <div style={{ textAlign: 'center', padding: '60px 24px', color: C.text3 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3, marginBottom: 12 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <div style={{ fontSize: 14 }}>Kontakt suchen</div>
          </div>
        )}
      </div>
    </>
  );
}

function EmptyMobileState({ onBackHome }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 280 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>Ansicht nicht verfügbar</div>
        <div style={{ fontSize: 13, color: C.text3, marginBottom: 14 }}>Diese Sektion ist auf Mobil gerade nicht aktiv. Über das Menü → Desktop-Ansicht erreichst du alle Funktionen.</div>
        <button className="crm-pressable" onClick={onBackHome} style={{ height: 36, padding: '0 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text2, fontSize: 13, fontWeight: 600 }}>
          Zurück zu Home
        </button>
      </div>
    </div>
  );
}

/* ─────────── Main MobileLayout ─────────── */
export default function MobileLayout({
  leads, tasks, meetings, statusMeta, authUsers = [], lists,
  bestandskunden = [],
  currentUser, notifications = [],
  onUpdateLead, onSaveTask, onToggleTask, onCreateContact, onMergeIntoLead,
  onSaveMeeting,
  onMarkAllNotifsRead, onLogout,
  auditLog = [],
  statusList = [],
  onMergeLeads,
  onQuickTask,
  onQuickNote,
  onQuickMail,
  onCarModeComplete,
  tableDensity = 'comfortable',
  theme,
  onToggleTheme,
}) {
  const [activeTab, setActiveTab]     = useState('home');
  const [contactDetail, setContactDetail] = useState(null);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [carMode, setCarMode] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(() => {
    try { return JSON.parse(localStorage.getItem('crm_last_sync_at')) || null; } catch { return null; }
  });
  const pullStart = React.useRef(null);
  const [activeSession, setActiveSession] = useState(null); // { session, resumeLeads }
  const [netOnline, setNetOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true));
  const [offlineSnap, setOfflineSnap] = useState(null);
  const [cloudSyncing, setCloudSyncing] = useState(false);

  useEffect(() => {
    const sync = () => {
      const o = typeof navigator !== 'undefined' ? navigator.onLine : true;
      setNetOnline(o);
      if (!o) setOfflineSnap(loadOfflineSnapshot());
      else setOfflineSnap(null);
    };
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    if (typeof navigator !== 'undefined' && !navigator.onLine) setOfflineSnap(loadOfflineSnapshot());
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  /* Auto-pull team cloud on mobile start */
  useEffect(() => {
    if (!currentUser?.id) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    setCloudSyncing(true);
    pullTeamCrmIntoLocal()
      .catch(() => {})
      .finally(() => setCloudSyncing(false));
  }, [currentUser?.id]); // eslint-disable-line

  /* Pull-to-Refresh: Sync-Ende beobachten */
  useEffect(() => {
    function onSynced() {
      setRefreshing(false);
      try { setLastSyncAt(JSON.parse(localStorage.getItem('crm_last_sync_at')) || null); } catch {}
    }
    window.addEventListener('crm-synced', onSynced);
    return () => window.removeEventListener('crm-synced', onSynced);
  }, []);

  function findScrollTop(el) {
    let n = el;
    while (n && n !== document.body) {
      if (n.scrollHeight > n.clientHeight + 5) return n.scrollTop;
      n = n.parentElement;
    }
    return 0;
  }
  function handlePullStart(e) {
    if (refreshing) return;
    const t = e.touches[0];
    pullStart.current = findScrollTop(e.target) <= 0 ? { y: t.clientY } : null;
  }
  function handlePullMove(e) {
    const st = pullStart.current;
    if (!st) return;
    const dy = e.touches[0].clientY - st.y;
    setPullPx(dy > 8 ? Math.min(80, (dy - 8) * 0.45) : 0);
  }
  function handlePullEnd() {
    if (pullPx > 52 && !refreshing) {
      setRefreshing(true);
      window.dispatchEvent(new CustomEvent('crm-request-resync'));
      // Fallback, falls kein crm-synced kommt
      setTimeout(() => setRefreshing(false), 6000);
    }
    setPullPx(0);
    pullStart.current = null;
  }

  const leadsForUI = useMemo(() => {
    if (netOnline) return leads;
    const snap = offlineSnap;
    if (!snap?.leads?.length) return leads;
    return snap.leads.map(sl => leads.find(l => String(l.id) === String(sl.id)) || sl);
  }, [netOnline, leads, offlineSnap]);

  const unread   = notifications.filter(n => !n.read).length;
  const openTasks = tasks.filter(t => !t.done).length;

  const TAB_TITLES = { home: 'MittelBoost', contacts: 'Kontakte', pipeline: 'Pipeline', tasks: 'Aufgaben', termine: 'Termine', search: 'Suche', analytics: 'Analytics', insights: 'Insights' };

  function goTab(tab) {
    if (tab === 'car_mode') {
      setCarMode(true);
      setDrawerOpen(false);
      return;
    }
    if (tab === 'call_sessions') {
      setShowSessions(true);
      setDrawerOpen(false);
      return;
    }
    if (tab === 'desktop_view') {
      setDrawerOpen(false);
      setViewMode('desktop');
      return;
    }
    setActiveTab(tab);
    setContactDetail(null);
  }
  function openContact(lead) {
    const merged = leadsForUI.find(l => String(l.id) === String(lead.id)) || lead;
    setContactDetail(merged);
    setActiveTab('contacts');
  }

  const isDetail = !!contactDetail;
  // Immer die frische Lead-Version aus dem State anzeigen (sonst bleiben Status-/Feldänderungen unsichtbar)
  const detailLead = isDetail
    ? (leadsForUI.find(l => String(l.id) === String(contactDetail.id)) || contactDetail)
    : null;
  const title = isDetail ? detailLead.name : TAB_TITLES[activeTab] || 'CRM';

  if (carMode) {
    return (
      <CarModeView
        leads={leadsForUI}
        bestandskunden={bestandskunden}
        statusMeta={statusMeta}
        currentUser={currentUser}
        onExit={() => setCarMode(false)}
        onSessionComplete={onCarModeComplete}
      />
    );
  }

  return (
    <div className="crm-mobile-fade-in" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      {/* Global header */}
      <Header
        title={title}
        onMenu={() => setDrawerOpen(true)}
        onBack={isDetail ? () => setContactDetail(null) : null}
        onAdd={activeTab === 'contacts' && !isDetail ? () => setShowNewContact(true) : null}
        notifCount={unread}
        onNotif={() => setShowNotifs(true)}
      />
      {!netOnline && (
        <div style={{ flexShrink: 0, padding: '8px 14px', background: '#fef9c3', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: '#854d0e', fontWeight: 600 }}>
          Offline — {offlineSnap?.at ? `zuletzt ${new Date(offlineSnap.at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}` : 'gespeicherter Lese-Cache'}
          {(!offlineSnap?.leads?.length && !leadsForUI.length) ? ' · Keine gecachten Kontakte' : ''}
        </div>
      )}
      {cloudSyncing && leadsForUI.length > 0 && (
        <div style={{ flexShrink: 0, padding: '5px 14px', background: 'var(--accent-bg)', borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.accent, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.66 0 3-4.03 3-9s-1.34-9-3-9m0 18c-1.66 0-3-4.03-3-9s1.34-9 3-9m-9 9a9 9 0 019-9" strokeLinecap="round"/></svg>
          Cloud-Daten werden geladen…
        </div>
      )}

      {/* Pull-to-Refresh Indikator */}
      {(pullPx > 0 || refreshing) && (
        <div style={{ flexShrink: 0, height: refreshing ? 34 : pullPx, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, overflow: 'hidden', transition: refreshing ? 'height .15s ease' : 'none', color: C.text2, fontSize: 12, fontWeight: 600 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ animation: refreshing ? 'spin 0.9s linear infinite' : 'none', transform: refreshing ? 'none' : `rotate(${pullPx * 4}deg)` }}>
            <path d="M21 12a9 9 0 11-2.64-6.36" strokeLinecap="round"/><polyline points="21 3 21 9 15 9" strokeLinecap="round"/>
          </svg>
          {refreshing ? 'Synchronisiere…' : pullPx > 52 ? 'Loslassen zum Aktualisieren' : lastSyncAt ? `Zuletzt: ${new Date(lastSyncAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr` : 'Ziehen zum Aktualisieren'}
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        onTouchStart={handlePullStart} onTouchMove={handlePullMove} onTouchEnd={handlePullEnd}>
        <div key={isDetail ? `detail-${contactDetail?.id}` : activeTab} className={isDetail ? 'crm-mobile-tab-panel crm-detail-enter' : 'crm-mobile-tab-panel'} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {isDetail ? (
            <ContactDetail lead={detailLead} statusMeta={statusMeta} lists={lists} onUpdateLead={onUpdateLead} onSaveTask={onSaveTask} onQuickTask={onQuickTask}/>
          ) : (cloudSyncing && leadsForUI.length === 0 && ['home', 'contacts', 'pipeline', 'search'].includes(activeTab)) ? (
            <SkeletonRows/>
          ) : (
            <>
              {activeTab === 'home' && (
                <HomeView leads={leadsForUI} tasks={tasks} meetings={meetings} statusMeta={statusMeta} onOpenContact={openContact} onOpenTab={goTab} onToggleTask={onToggleTask} onOpenSessions={() => setShowSessions(true)} currentUser={currentUser} authUsers={authUsers}/>
              )}
              {activeTab === 'contacts' && (
                <ContactsView leads={leadsForUI} statusMeta={statusMeta} lists={lists} onUpdateLead={onUpdateLead} onSaveTask={onSaveTask} onOpenContact={openContact} onMergeLeads={onMergeLeads} onQuickTask={onQuickTask} onQuickNote={onQuickNote} onQuickMail={onQuickMail} tableDensity={tableDensity}
                  onStartSession={sessionLeads => setActiveSession({ session: { id: Date.now(), index: 0, done: [] }, resumeLeads: sessionLeads })}
                />
              )}
              {activeTab === 'pipeline' && (
                <PipelineView leads={leadsForUI} statusMeta={statusMeta} onOpenLead={openContact}/>
              )}
              {activeTab === 'tasks' && (
                <TasksView tasks={tasks} onToggleTask={onToggleTask}/>
              )}
              {activeTab === 'termine' && (
                <TermineView meetings={meetings} leads={leadsForUI} onSaveMeeting={onSaveMeeting} onOpenContact={openContact}/>
              )}
              {activeTab === 'search' && (
                <SearchView leads={leadsForUI} onOpenContact={c => { openContact(c); }}/>
              )}
              {activeTab === 'analytics' && (
                <AnalyticsView leads={leadsForUI} tasks={tasks} meetings={meetings} statusMeta={statusMeta} authUsers={authUsers} currentUser={currentUser} />
              )}
              {activeTab === 'insights' && (
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <InsightsView leads={leadsForUI} meetings={meetings} auditLog={auditLog} statusList={statusList} authUsers={authUsers} currentUser={currentUser} />
                </div>
              )}
              {!['home', 'contacts', 'pipeline', 'tasks', 'termine', 'search', 'analytics', 'insights'].includes(activeTab) && (
                <EmptyMobileState onBackHome={() => goTab('home')} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Bottom tabs */}
      <BottomTabs active={activeTab} onSelect={goTab} taskBadge={openTasks}/>

      {/* Drawer */}
      {drawerOpen && (
        <Drawer currentUser={currentUser} onClose={() => setDrawerOpen(false)} onNavigate={goTab} onLogout={onLogout} theme={theme} onToggleTheme={onToggleTheme}/>
      )}

      {/* Notifications */}
      {showNotifs && (
        <NotificationsSheet
          notifications={notifications}
          onMarkAllRead={onMarkAllNotifsRead}
          onClose={() => setShowNotifs(false)}
        />
      )}

      {/* New contact modal */}
      {showNewContact && (
        <NewContactModal
          onClose={() => setShowNewContact(false)}
          onCreate={lead => { onCreateContact(lead); setShowNewContact(false); }}
          onMergeIntoLead={onMergeIntoLead}
          existingLeads={leadsForUI}
          isMobileFullscreen
        />
      )}

      {/* Call Sessions Modal */}
      {showSessions && !activeSession && (
        <CallSessionsModal
          allLeads={leadsForUI}
          onClose={() => setShowSessions(false)}
          onResume={(session, resumeLeads) => { setActiveSession({ session, resumeLeads }); setShowSessions(false); }}
          onNew={() => { setShowSessions(false); setActiveTab('contacts'); }}
        />
      )}

      {/* Active call session → Fahrmodus UI */}
      {activeSession && (
        <CarModeView
          leads={activeSession.resumeLeads}
          bestandskunden={bestandskunden}
          statusMeta={statusMeta}
          currentUser={currentUser}
          initialSession={activeSession}
          onExit={() => setActiveSession(null)}
          onSessionComplete={(...args) => { setActiveSession(null); onCarModeComplete?.(...args); }}
        />
      )}
    </div>
  );
}
